import { compareStrings } from '../util.js';
import type { MapFlow, MapModel, StepType } from './model.js';

/**
 * The lane grid (ADR-017 §2, Decision 54.32), in diagram units: the
 * prototype's grid. Lane order gives a step its row; flows give it its column.
 * Nothing here is stored. Every version is laid out again by this code, so the
 * same map looks the same everywhere.
 */
export const MAP_GRID = {
  originX: 20,
  originY: 20,
  /** The band on the left of the pool that holds the client's name. */
  poolLabelWidth: 30,
  /** The band on the left of each lane that holds the lane's name. */
  laneLabelWidth: 30,
  padding: 16,
  columnWidth: 150,
  laneHeight: 100,
} as const;

export interface Point {
  x: number;
  y: number;
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StepPlacement extends Bounds {
  row: number;
  column: number;
}

export interface MapLayout {
  pool: Bounds;
  /** By lane key, top to bottom. */
  lanes: Map<string, Bounds>;
  /** By step key, in the model's order. */
  steps: Map<string, StepPlacement>;
  /** Orthogonal waypoints by flow key. */
  flows: Map<string, Point[]>;
  rows: number;
  columns: number;
}

/** Shape sizes, as bpmn-js draws events, decisions and tasks. */
export function stepSize(type: StepType): { width: number; height: number } {
  if (type === 'start_event' || type === 'end_event') return { width: 36, height: 36 };
  if (type === 'exclusive_gateway') return { width: 50, height: 50 };
  return { width: 118, height: 70 };
}

/**
 * Lays a map out on the lane grid. Deterministic: the same model always gives
 * the same layout, whatever the order of its flows.
 *
 * Columns come from longest-path layering from the start events (ADR-017 §2):
 *
 * 1. The main path first: the steps reached from a start along flows that are
 *    not exceptions. Back edges (a flow returning to an earlier step) are set
 *    aside. Each step goes in the first free cell of its lane at or after the
 *    column after all its predecessors. Ties go to the earlier step in the
 *    model's order (its `sequence`).
 * 2. Then the rest: a step reached only through an exception starts in the
 *    column of the step it branches from (in another lane, as the prototype
 *    draws "Confirm the goods were received" under "Matches?"), so exception
 *    branches never push the main path along. Anything unreachable (only in an
 *    invalid map) comes last.
 * 3. Empty columns are closed up.
 *
 * Flows are routed orthogonally (`edgePath`). The model must have passed
 * `validateMapModel`; a step in a missing lane is drawn in the first row.
 */
export function layoutMap(model: MapModel): MapLayout {
  const rowOfLane = new Map(model.lanes.map((lane, i) => [lane.key, i]));
  const order = new Map(model.steps.map((step, i) => [step.key, i]));
  const rowOf = new Map(model.steps.map((s) => [s.key, rowOfLane.get(s.lane) ?? 0]));

  const out = new Map<string, MapFlow[]>();
  for (const flow of model.flows) {
    if (!order.has(flow.from) || !order.has(flow.to) || flow.from === flow.to) continue;
    out.set(flow.from, [...(out.get(flow.from) ?? []), flow]);
  }
  for (const list of out.values())
    list.sort((a, b) => order.get(a.to)! - order.get(b.to)! || compareStrings(a.key, b.key));

  const grid: Grid = { order, rowOf, column: new Map(), used: new Map() };

  const starts = model.steps.filter((s) => s.type === 'start_event').map((s) => s.key);
  const mainOut = (key: string) => (out.get(key) ?? []).filter((f) => !f.is_exception);
  const main = reach(starts, mainOut);
  const mainSteps = model.steps.filter((s) => main.has(s.key)).map((s) => s.key);
  place(
    grid,
    mainSteps,
    starts,
    (key) => mainOut(key).filter((f) => main.has(f.to)),
    () => 1,
    new Map(),
  );

  const rest = new Set(model.steps.filter((s) => !main.has(s.key)).map((s) => s.key));
  const weight = (f: MapFlow) => (f.is_exception ? 0 : 1);
  const bound = new Map<string, number>();
  for (const [from, flows] of out) {
    const col = grid.column.get(from);
    if (col === undefined) continue;
    for (const f of flows)
      if (rest.has(f.to)) bound.set(f.to, Math.max(bound.get(f.to) ?? 0, col + weight(f)));
  }
  const restSteps = [...rest];
  const restRoots = [...restSteps].sort(
    (a, b) =>
      Number(!bound.has(a)) - Number(!bound.has(b)) ||
      (bound.get(a) ?? 0) - (bound.get(b) ?? 0) ||
      order.get(a)! - order.get(b)!,
  );
  place(
    grid,
    restSteps,
    restRoots,
    (key) => (out.get(key) ?? []).filter((f) => rest.has(f.to)),
    weight,
    bound,
  );

  const usedColumns = [...new Set(grid.column.values())].sort((a, b) => a - b);
  const compact = new Map(usedColumns.map((c, i) => [c, i]));

  const g = MAP_GRID;
  const rows = model.lanes.length;
  const columns = usedColumns.length;
  const width = g.poolLabelWidth + g.laneLabelWidth + g.padding * 2 + columns * g.columnWidth;
  const left = g.originX + g.poolLabelWidth + g.laneLabelWidth + g.padding;

  const lanes = new Map<string, Bounds>();
  model.lanes.forEach((lane, i) => {
    lanes.set(lane.key, {
      x: g.originX + g.poolLabelWidth,
      y: g.originY + i * g.laneHeight,
      width: width - g.poolLabelWidth,
      height: g.laneHeight,
    });
  });

  const steps = new Map<string, StepPlacement>();
  for (const step of model.steps) {
    const row = rowOf.get(step.key)!;
    const column = compact.get(grid.column.get(step.key)!)!;
    const { width: w, height: h } = stepSize(step.type);
    const cx = left + column * g.columnWidth + g.columnWidth / 2;
    const cy = g.originY + row * g.laneHeight + g.laneHeight / 2;
    steps.set(step.key, { x: cx - w / 2, y: cy - h / 2, width: w, height: h, row, column });
  }

  const flows = new Map<string, Point[]>();
  for (const flow of model.flows) {
    const a = steps.get(flow.from);
    const b = steps.get(flow.to);
    if (a && b && a !== b) flows.set(flow.key, edgePath(a, b));
  }

  return {
    pool: { x: g.originX, y: g.originY, width, height: rows * g.laneHeight },
    lanes,
    steps,
    flows,
    rows,
    columns,
  };
}

/**
 * Orthogonal routing on the lane grid (the prototype's `edgePath`). Forward
 * flows leave the right side and enter the left side, turning in the gap just
 * before the target's column; flows in one column run straight up or down;
 * backward flows go back along the source's row and drop into the target, or,
 * in the same lane, loop underneath both shapes.
 */
export function edgePath(a: StepPlacement, b: StepPlacement): Point[] {
  const ac = centre(a);
  const bc = centre(b);
  if (b.column > a.column) {
    const sx = a.x + a.width;
    if (a.row === b.row) return [pt(sx, ac.y), pt(b.x, bc.y)];
    const turn = b.x - 20;
    return [pt(sx, ac.y), pt(turn, ac.y), pt(turn, bc.y), pt(b.x, bc.y)];
  }
  const down = b.row > a.row;
  if (b.column === a.column) {
    return [pt(ac.x, down ? a.y + a.height : a.y), pt(bc.x, down ? b.y : b.y + b.height)];
  }
  if (a.row !== b.row) {
    return [pt(a.x, ac.y), pt(bc.x, ac.y), pt(bc.x, down ? b.y : b.y + b.height)];
  }
  // Under both shapes, still inside the lane.
  const below = Math.max(a.y + a.height, b.y + b.height) + 10;
  return [pt(ac.x, a.y + a.height), pt(ac.x, below), pt(bc.x, below), pt(bc.x, b.y + b.height)];
}

/** The grid cell a point falls in, for reading a drop on the canvas. */
export function cellAt(layout: MapLayout, point: Point): { row: number; column: number } {
  const g = MAP_GRID;
  const left = g.originX + g.poolLabelWidth + g.laneLabelWidth + g.padding;
  const row = Math.floor((point.y - g.originY) / g.laneHeight);
  const column = Math.round((point.x - left - g.columnWidth / 2) / g.columnWidth);
  return {
    row: Math.min(Math.max(row, 0), Math.max(layout.rows - 1, 0)),
    column: Math.max(column, 0),
  };
}

export function centre(b: Bounds): Point {
  return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
}

// ---------------------------------------------------------------------------

interface Grid {
  order: Map<string, number>;
  rowOf: Map<string, number>;
  /** Assigned columns, before closing up. */
  column: Map<string, number>;
  /** Columns taken in each row. */
  used: Map<number, Set<number>>;
}

/** The steps reachable from `roots` along `next`. */
function reach(roots: readonly string[], next: (key: string) => MapFlow[]): Set<string> {
  const seen = new Set<string>();
  const stack = [...roots];
  while (stack.length > 0) {
    const key = stack.pop()!;
    if (seen.has(key)) continue;
    seen.add(key);
    for (const f of next(key)) stack.push(f.to);
  }
  return seen;
}

/**
 * Places `members` in topological order of their forward edges (back edges
 * found depth-first from `roots` are set aside). The next step placed is the
 * ready one with the smallest lower bound, then the earliest in the model; it
 * takes the first free column of its row at or after its bound.
 */
function place(
  grid: Grid,
  members: readonly string[],
  roots: readonly string[],
  edges: (key: string) => MapFlow[],
  weight: (flow: MapFlow) => number,
  bound: Map<string, number>,
): void {
  const back = new Set<MapFlow>();
  const state = new Map<string, 'open' | 'done'>();
  const visit = (key: string): void => {
    state.set(key, 'open');
    for (const f of edges(key)) {
      const s = state.get(f.to);
      if (s === 'open') back.add(f);
      else if (s === undefined) visit(f.to);
    }
    state.set(key, 'done');
  };
  for (const key of [...roots, ...members]) if (!state.has(key)) visit(key);

  const waiting = new Map(members.map((key) => [key, 0]));
  for (const key of members)
    for (const f of edges(key)) if (!back.has(f)) waiting.set(f.to, waiting.get(f.to)! + 1);

  const ready = members.filter((key) => waiting.get(key) === 0);
  while (ready.length > 0) {
    let pick = 0;
    for (let i = 1; i < ready.length; i += 1) {
      const a = ready[i]!;
      const b = ready[pick]!;
      const byBound = (bound.get(a) ?? 0) - (bound.get(b) ?? 0);
      if (byBound < 0 || (byBound === 0 && grid.order.get(a)! < grid.order.get(b)!)) pick = i;
    }
    const key = ready.splice(pick, 1)[0]!;
    const row = grid.rowOf.get(key)!;
    const used = grid.used.get(row) ?? new Set<number>();
    let column = bound.get(key) ?? 0;
    while (used.has(column)) column += 1;
    used.add(column);
    grid.used.set(row, used);
    grid.column.set(key, column);
    for (const f of edges(key)) {
      if (back.has(f)) continue;
      bound.set(f.to, Math.max(bound.get(f.to) ?? 0, column + weight(f)));
      const left = waiting.get(f.to)! - 1;
      waiting.set(f.to, left);
      if (left === 0) ready.push(f.to);
    }
  }
}

function pt(x: number, y: number): Point {
  return { x, y };
}
